export type InputSnapshot = {
  axisX: -1 | 0 | 1;
  axisY: -1 | 0 | 1;
  fireHeld: boolean;
};

const ownedCodes = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
]);

export class KeyboardInputAdapter {
  private readonly held = new Set<string>();
  private suppressFire = false;

  public constructor(private readonly ownsInput: () => boolean) {}

  public attach(): () => void {
    const down = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (!this.ownsInput()) return;
      if (ownedCodes.has(event.code) && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
      }
      this.held.add(event.code);
    };
    const up = (event: KeyboardEvent): void => {
      this.held.delete(event.code);
      if (event.code === 'Space') this.suppressFire = false;
    };
    const reset = (): void => this.held.clear();
    window.addEventListener('keydown', down, { passive: false });
    window.addEventListener('keyup', up);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', reset);
    };
  }

  public consumeStartSpace(): void {
    this.suppressFire = true;
  }

  public clearFireSuppression(): void {
    this.suppressFire = false;
  }

  public reset(): void {
    this.held.clear();
    this.suppressFire = true;
  }

  public snapshot(touchAxis = { x: 0, y: 0 }, touchFire = false): InputSnapshot {
    const left = this.held.has('KeyA') || this.held.has('ArrowLeft') || touchAxis.x < -0.25;
    const right = this.held.has('KeyD') || this.held.has('ArrowRight') || touchAxis.x > 0.25;
    const up = this.held.has('KeyW') || this.held.has('ArrowUp') || touchAxis.y < -0.25;
    const down = this.held.has('KeyS') || this.held.has('ArrowDown') || touchAxis.y > 0.25;
    return {
      axisX: left === right ? 0 : left ? -1 : 1,
      axisY: up === down ? 0 : up ? -1 : 1,
      fireHeld: !this.suppressFire && (this.held.has('Space') || touchFire),
    };
  }
}
